
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Identification
 *
 * A single identity document record linked to a contact, capturing the document type (e.g. Driver's License, Passport, Social Security Number, Green Card, USCIS Number, Bar Number), the document number (stored encrypted), the issuing authority, issue and expiry dates, and optional photo attachments. Used to record and verify the identity of clients and other contacts, including immigration documents and attorney bar credentials.
 */
class Identification extends Base implements EntityType {
    identificationId: uuid;
    method: string;
    methodType: uuid;
    identifier?: string | null;
    issuer?: string | null;
    issuedOn?: string | null;
    expiresOn?: string | null;
    additionalInfo?: string | null;
    attachments: uuid[];

    /**
     * Create a new Identification, with an (optional) JSON object or Identification class
     *
     * @param {Object|Identification} doc - Can be either a simple JSON object or a Identification class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Identification | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            identificationId: genUUID(),
            method: null,
            methodType: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'identification';
        // @ts-ignore
        this.identificationId = doc._identificationId || doc.identificationId || doc.identificationid || genUUID();
        // @ts-ignore
        this.method = doc._method || doc.method || null;
        // @ts-ignore
        this.methodType = doc._methodType || doc.methodType || doc.methodtype || null;
        // @ts-ignore
        this.identifier = doc._identifier || doc.identifier || null;
        // @ts-ignore
        this.issuer = doc._issuer || doc.issuer || null;
        // @ts-ignore
        this.issuedOn = doc._issuedOn || doc.issuedOn || doc.issuedon || null;
        // @ts-ignore
        this.expiresOn = doc._expiresOn || doc.expiresOn || doc.expireson || null;
        // @ts-ignore
        this.additionalInfo = doc._additionalInfo || doc.additionalInfo || doc.additionalinfo || null;
        // @ts-ignore
        this.attachments = doc._attachments || doc.attachments || [];

    }
    /**
     * @returns {uuid} This is a generic pointer to the identificationId across all entity types
     */
    get id(): uuid {
        return this.identificationId;
    }


    /**
     * Add a attachment to identification.attachments
     *
     * @param {uuid} attachment - The Attachment to add
     * @returns uuid - The attachment with any defaulted fields set
     */
    addAttachment(attachment: uuid) : uuid {
        if (!attachment) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.attachments) this.attachments = [];
        this.attachments.push(attachment);

        return attachment;
    }

    /**
     * Update a attachment from identification
     *
     * @param {uuid} attachment - The Attachment to remove
     */
    updateAttachment(attachment: uuid) {
        if (!this.attachments) this.attachments = [];
        
        // Find the matching object by attachment.attachmentId
        const index = this.attachments.findIndex(item => item === attachment);
        if (index !== -1) {
            this.attachments[index] = attachment;
        }
        else this.addAttachment(attachment);
    }

    /**
     * Remove a attachment from identification
     *
     * @param {uuid} attachment - The Attachment to remove
     */
    removeAttachment(attachment: uuid) {
        if (!this.attachments) return;
        
        // Find the matching object by attachment.attachmentId
        let foundItems = this.attachments?.filter(item => item === attachment);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.attachments = this.attachments.filter(item => !(item === attachment));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonAttachments() {
        this.attachments = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for identification"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.identificationId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["attachments"];
    }

    /**
     * Default entries are Outlaw-supplied Identification entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Identification[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'identification',
            table: 'IDENTIFICATION',
            comment: `A single identity document record linked to a contact, capturing the document type (e.g. Driver's License, Passport, Social Security Number, Green Card, USCIS Number, Bar Number), the document number (stored encrypted), the issuing authority, issue and expiry dates, and optional photo attachments. Used to record and verify the identity of clients and other contacts, including immigration documents and attorney bar credentials.`,
            fields: {
                identificationId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for identification'
                },
                method: {
                    type: 'string',
                    required: true,
                    comment: 'The kind of ID, e.g. passport'
                },
                methodType: {
                    type: 'uuid',
                    entityType: 'lookup',
                    lookupPaths: ['contact:identification:method'],
                    required: true,
                    comment: 'Method of identification lookup'
                },
                identifier: {
                    type: 'string',
                    encrypted: true,
                    comment: 'The document or ID number (e.g. license or passport number). Stored encrypted.'
                },
                issuer: {
                    type: 'string',
                    comment: 'What authority issued this ID?'
                },
                issuedOn: {
                    type: 'string',
                    comment: 'Date of issue, in whatever format the document specifies'
                },
                expiresOn: {
                    type: 'string',
                    comment: 'Date of expiration, in whatever format the document specifies'
                },
                additionalInfo: {
                    type: 'string',
                    comment: 'Any other information from the method that is relevant'
                },
                attachments: {
                    type: 'uuid[]',
                    entityType: 'document',
                    comment: 'Photos of the document, usually front and back, but could be multiple pages'
                }
            },
            maps: [{
                    name: 'entityId',
                    keysFn: a => a.entityId,
                    presentFn: (a, k) => a.entityId === k && a.status !== 'D'
                },
                {
                    name: 'methodType',
                    keysFn: t => t.methodType,
                    presentFn: (t, k) => t.methodType === k
                },
                {
                    name: 'attachments',
                    keysFn: t => t.attachments,
                    presentFn: (t, k) => t.attachments?.includes(k)
                }
            ]
        });
    }

}
export default Identification;
